import { MSCameraConfig, MSLatLng, MSLatLngBounds, MSPoint, MSPointBounds } from 'src/app/models/map.model';
import { CONSTANTS } from 'src/app/services/config.service';

export function getCameraConfigForPoints(currentZoom: number, currentBounds: MSLatLngBounds, points: MSLatLng[]): MSCameraConfig {
  const bounds = getBoundsAroundPoints(points);
  const additionalZoom = getZoomToBounds(currentBounds, bounds);
  const totalZoom = currentZoom + additionalZoom;

  const cameraConfig: MSCameraConfig = {
    center: bounds.center,
    zoom: totalZoom
  };

  return cameraConfig;
}

export function getBoundsAroundPoints(points: MSLatLng[]): MSLatLngBounds {
  const center = calcCenter(points);
  const southwest = calcSouthwest(points);
  const northeast = calcNortheast(points);
  const bounds: MSLatLngBounds = {
    center,
    southwest,
    northeast
  };

  return bounds;
}

function calcCenter(points: MSLatLng[]): MSLatLng {
  const southWest = calcSouthwest(points);
  const northEast = calcNortheast(points);
  const center: MSLatLng = {
    lat: (southWest.lat + northEast.lat) / 2,
    lng: (southWest.lng + northEast.lng) / 2
  };

  return center;
}

function calcSouthwest(points: MSLatLng[]): MSLatLng {
  const southwest = points.reduce((previousValue: MSLatLng, currentValue: MSLatLng, currentIndex: number, array: MSLatLng[]) => {
    const lat = Math.min(previousValue.lat, currentValue.lat);
    const lng = Math.min(previousValue.lng, currentValue.lng);
    const result: MSLatLng = { lat, lng };

    return result;
  });

  return southwest;
}

function calcNortheast(points: MSLatLng[]): MSLatLng {
  const northEast = points.reduce((previousValue: MSLatLng, currentValue: MSLatLng, currentIndex: number, array: MSLatLng[]) => {
    const lat = Math.max(previousValue.lat, currentValue.lat);
    const lng = Math.max(previousValue.lng, currentValue.lng);
    const result: MSLatLng = { lat, lng };

    return result;
  });

  return northEast;
}

function getZoomToBounds(currentBounds: MSLatLngBounds, bounds: MSLatLngBounds): number {
  const zoom = getScaleOfInscribedBoundRectangle(currentBounds, bounds);
  return zoom;
}

function getScaleOfInscribedBoundRectangle(outerBounds: MSLatLngBounds, innerBounds: MSLatLngBounds): number {
  const innerPointBounds = boundsToPointBounds(innerBounds);
  const outerPointBounds = boundsToPointBounds(outerBounds);

  const innerBoundVerDist = Math.abs(innerPointBounds.southwest.y - innerPointBounds.northeast.y);
  const innerBoundHorDist = Math.abs(innerPointBounds.southwest.x - innerPointBounds.northeast.x);

  const outerBoundVerDist = Math.abs(outerPointBounds.southwest.y - outerPointBounds.northeast.y);
  const outerBoundHorDist = Math.abs(outerPointBounds.southwest.x - outerPointBounds.northeast.x);

  const maxVerScale = outerBoundVerDist / innerBoundVerDist;
  const maxHorScale = outerBoundHorDist / innerBoundHorDist;

  const maxScaleOfInscribedBoundRectangle = Math.min(maxVerScale, maxHorScale);
  const zoom = Math.log2(maxScaleOfInscribedBoundRectangle);

  return zoom;
}

function boundsToPointBounds(bounds: MSLatLngBounds): MSPointBounds {
  const southWestBounds = latLngToPoint(bounds.southwest);
  const northEastBounds = latLngToPoint(bounds.northeast);
  const centerBounds = latLngToPoint(bounds.center);

  const pointBounds: MSPointBounds = {
    southwest: southWestBounds,
    northeast: northEastBounds,
    center: centerBounds
  };

  return pointBounds;
}

function latLngToPoint(latLng: MSLatLng): MSPoint {
  const point: MSPoint = {
    x: lngToX(latLng.lng),
    y: latToY(latLng.lat)
  };

  return point;
}

function lngToX(lng: number): number {
  const x = CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_WIDTH * ((lng + 180) / 360);

  return x;
}

function latToY(lat: number): number {
  if (lat > 85.05113) {
    return CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT;
  } else if (lat < -85.05113) {
    return 0;
  }

  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT / 2 - (CONSTANTS.MAPS.GOOGLE_MAP_PIXEL_HEIGHT * mercN) / (2 * Math.PI);

  return y;
}
